import React, { useState, useEffect, useRef } from "react"
import { Form } from "react-bootstrap"
import { arrowDownIcon, arrowUpIcon, folderIcon } from "../../constants/icons"
import CheckboxSection from "./CheckboxSection"
import { useLocation } from "react-router-dom"

function MultipleCheckbox({ data, selectedChapters, setSelectedChapters }) {
  const [expandedSections, setExpandedSections] = useState({})
  const location = useLocation()

  const topicId = location.state?.topicId;

  useEffect(() => {
    const newSelectedChapters = { ...selectedChapters };
    let changed = false;

    // Synchronize parent topic checkboxes based on their children's state
    data.forEach((topic) => {
      const allSubtopicsSelected =
        topic.subTopics.length > 0 &&
        topic.subTopics.every((subTopic) => newSelectedChapters[subTopic._id]);

      if (newSelectedChapters[topic._id] !== allSubtopicsSelected) {
        newSelectedChapters[topic._id] = allSubtopicsSelected;
        changed = true;
      }
    });

    if (changed) {
      setSelectedChapters(newSelectedChapters);
    }
  }, [selectedChapters, data, setSelectedChapters]);

  useEffect(() => {
    if (data.length > 0) {
      const initialSelectedChapters = {};
      const initialExpandedSections = {};
      let preSelectionMade = false;

      data.forEach((topic) => {
        const isTopicSelected = topicId && topic._id === topicId;
        if (isTopicSelected) {
          preSelectionMade = true;
        }
        initialSelectedChapters[topic._id] = isTopicSelected;
        topic.subTopics.forEach((subTopic) => {
          initialSelectedChapters[subTopic._id] = isTopicSelected;
        });
        initialExpandedSections[topic._id] = isTopicSelected;
      });

      if (preSelectionMade) {
        setSelectedChapters(initialSelectedChapters);
        setExpandedSections(initialExpandedSections);
      }
    }
  }, [data, topicId]);

  const parentCheckboxRef = useRef(null);

  const handleChildCheckboxChange = (key) => {
    setSelectedChapters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const toggleAllChildCheckboxes = (sectionId, isChecked) => {
    const newState = { ...selectedChapters }
    newState[sectionId] = isChecked
    const topic = data.find((t) => t._id === sectionId)
    if (topic) {
      topic.subTopics.forEach((subTopic) => {
        newState[subTopic._id] = isChecked
      })
    }
    setSelectedChapters(newState)
  }

  const allSelected =
    data.length > 0 &&
    data.every((topic) => {
      return (
        selectedChapters[topic._id] &&
        topic.subTopics.every((subTopic) => selectedChapters[subTopic._id])
      )
    })

  const someSelected =
    data.some((topic) => {
      return (
        selectedChapters[topic._id] ||
        topic.subTopics.some((subTopic) => selectedChapters[subTopic._id])
      )
    }) && !allSelected

  const selectedCount = Object.values(selectedChapters).filter(
    (val) => val
  ).length
  const parentCheckboxLabel =
    selectedCount === 0
      ? "All chapters"
      : `${selectedCount} Chapter${selectedCount !== 1 ? "s" : ""} selected`

  const resetCheckboxes = () => {
    const resetState = {}
    data.forEach((topic) => {
      resetState[topic._id] = false
      topic.subTopics.forEach((subTopic) => {
        resetState[subTopic._id] = false
      })
    })
    setSelectedChapters(resetState)
    setExpandedSections({})
  }

  useEffect(() => {
    if (parentCheckboxRef.current) {
      parentCheckboxRef.current.indeterminate = someSelected
    }
  }, [someSelected])

  return (
    <Form className='multiple-checkbox'>
      <Form.Group
        className='all-chapters-checkbox'
        controlId='formParentCheckbox'
      >
        <Form.Check
          type='checkbox'
          label={parentCheckboxLabel}
          checked={allSelected}
          onChange={() => {
            const newState = !allSelected
            const newSelectedChapters = {}
            data.forEach((topic) => {
              newSelectedChapters[topic._id] = newState
              topic.subTopics.forEach((subTopic) => {
                newSelectedChapters[subTopic._id] = newState
              })
            })
            setSelectedChapters(newSelectedChapters)
          }}
          ref={parentCheckboxRef}
          className='custom-checkbox'
        />
        <span role='button' className='text-btn' onClick={resetCheckboxes}>
          Reset
        </span>
      </Form.Group>
      <div className='checkbox-content-holder'>
        {data.map((topic) => (
          <CheckboxSection
            key={topic._id}
            sectionName={topic._id}
            label={
              <>
                {folderIcon} {topic.title}
              </>
            }
            selectedChapters={selectedChapters}
            setSelectedChapters={setSelectedChapters}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            toggleAllChildCheckboxes={toggleAllChildCheckboxes}
            handleChildCheckboxChange={handleChildCheckboxChange}
            arrowUpIcon={arrowUpIcon}
            arrowDownIcon={arrowDownIcon}
            childCheckboxes={topic.subTopics.map((subTopic) => ({
              name: subTopic._id,
              label: subTopic.title,
            }))}
          />
        ))}
      </div>
    </Form>
  )
}

export default MultipleCheckbox
